import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { dispatch, getState } from "../../state/store";
import {
  pushCreatedTableRowsIds,
  resetMarkets,
  setMarketIso,
  setMarketsError,
  setMarketsLoading,
  setMarketsRefresh,
  setUserMarketFilter,
  setUserMarkets
} from "../../state/ducks";

import { appUserService, log, marketsService, notificationService, t } from "../../services";
import { alignItems, marketNames, strCode } from "../../consts";
import { withSection } from "../../hoc";
import { responseUtil } from "../../utils";

import { MarketsTable, RemoveMarketModal } from "../../containers";
import { FlexContainers, PageContainer, WrapperContainer } from "../../components";
import MarketsModal from "../../containers/MarketManagement/MarketsModal/MarketsModal";
import { selectMarkets } from "../../state/selectors";

const MarketManagementPage = () => {
  const markets = useSelector((store) => store.markets?.markets);
  const loading = useSelector((store) => store.markets?.loading);
  const refresh = useSelector((store) => store.markets?.refresh);

  const [isMarketsModalActive, setIsMarketsModalActive] = useState(false);
  const [isRemoveMarketModalActive, setIsRemoveMarketModalActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [marketData, setMarketData] = useState(null);

  const onMarketsOpening = (market) => {
    setMarketData(market);
    setIsMarketsModalActive(true);
  };

  const onFormClose = () => {
    setIsMarketsModalActive(false);
    setMarketData(null);
  };

  const onRemoveMarket = () => {
    setIsMarketsModalActive(false);
    setIsRemoveMarketModalActive(true);
  };

  const onRemoveMarketCancel = () => {
    setIsRemoveMarketModalActive(false);
    setMarketData(null);
  };

  const fetchMarkets = useCallback(
    async (isRefresh) => {
      dispatch(setMarketsError(null));

      if ((markets !== null && !isRefresh) || loading) {
        return;
      }

      dispatch(setMarketsLoading(true));

      try {
        dispatch(resetMarkets());
        await marketsService.fetchMarkets();

        if (appUserService.isAdmin()) {
          const markets = selectMarkets()(getState());

          dispatch(
            setUserMarkets([marketNames.allMarkets, ...markets?.map((market) => market.market)])
          );
          dispatch(
            setUserMarketFilter(markets?.length > 1 ? marketNames.allMarkets : markets[0]?.market)
          );
          dispatch(setMarketIso(markets?.length > 1 ? marketNames.allMarkets : markets[0]?.market));
        }
      } catch (err) {
        dispatch(setMarketsError(err));

        log.error("MarketManagementPage: Failed to fetch markets", err);

        notificationService.showError({
          title: t(strCode.FETCH_MARKETS_ERROR),
          message: err?.response?.data?.message ? err.response.data.message : err?.toString()
        });
      } finally {
        dispatch(setMarketsLoading(false));
        dispatch(setMarketsRefresh(false));
      }
    },
    [refresh]
  );

  const onCreateMarket = useCallback(
    async (market) => {
      try {
        const res = await marketsService.createMarket(market);

        if (responseUtil.isResponseSuccessful(res)) {
          notificationService.showSuccess({
            title: t(strCode.SUCCESSFULLY_CREATED),
            message: t(strCode.NEW_MARKET_ADDED_DESCRIPTION)
          });

          dispatch(pushCreatedTableRowsIds([market.market]));
          dispatch(setMarketsRefresh(true));

          setIsMarketsModalActive(false);
        }
      } catch (err) {
        log.error("MarketManagementPage: Failed to create market", err);

        notificationService.showError({
          title: t(strCode.CREATE_MARKET_ERROR),
          message: err?.response?.data?.message ? err.response.data.message : err?.toString()
        });

        throw err;
      }
    },
    [refresh]
  );

  const onEditMarket = useCallback(
    async (market) => {
      try {
        const res = await marketsService.updateMarket(market);

        if (responseUtil.isResponseSuccessful(res)) {
          notificationService.showSuccess({
            title: t(strCode.UPDATE_MARKET_SUCCESS),
            message: t(strCode.UPDATE_MARKET_SUCCESS_INFO)
          });

          dispatch(setMarketsRefresh(true));

          setIsMarketsModalActive(false);
        }
      } catch (err) {
        log.error("MarketManagementPage: Failed to update market", err);

        notificationService.showError({
          title: t(strCode.UPDATE_MARKET_ERROR),
          message: err?.response?.data?.message ? err.response.data.message : err?.toString()
        });

        throw err;
      }
    },
    [refresh]
  );

  const onDeleteMarketConfirmation = useCallback(
    async (market) => {
      if (isLoading) return;

      try {
        setIsLoading(true);

        const res = await marketsService.deleteMarket(market);

        if (responseUtil.isResponseSuccessful(res)) {
          setMarketData(null);

          notificationService.showSuccess({
            title: t(strCode.DELETE_MARKET_SUCCESS),
            message: t(strCode.DELETE_MARKET_SUCCESS_INFO)
          });

          dispatch(setMarketsRefresh(true));

          setIsRemoveMarketModalActive(false);
        }
      } catch (err) {
        log.error("MarketManagementPage: Failed to delete market", err);

        notificationService.showError({
          title: t(strCode.DELETE_MARKET_ERROR),
          message: err?.response?.data?.message ? err.response.data.message : err?.toString()
        });

        log.error(`An error occurred while deleting market.`, err);
      } finally {
        setIsLoading(false);
      }
    },
    [refresh]
  );

  useEffect(() => {
    fetchMarkets(false);
  }, []);

  useEffect(() => {
    if (refresh) {
      fetchMarkets(true);
    }
  }, [refresh]);

  return (
    <PageContainer alignItems={alignItems.start}>
      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        width="100%">
        <MarketsTable marketsData={markets ? markets : []} onMarketsOpening={onMarketsOpening} />

        <MarketsModal
          data={marketData}
          isActive={isMarketsModalActive}
          onFormSave={marketData ? onEditMarket : onCreateMarket}
          onFormClose={onFormClose}
          onRemoveMarket={onRemoveMarket}
        />

        <RemoveMarketModal
          data={marketData}
          setActive={setIsRemoveMarketModalActive}
          isActive={isRemoveMarketModalActive}
          onRemoveMarketCancel={onRemoveMarketCancel}
          onDeleteMarketConfirmation={onDeleteMarketConfirmation}
          isLoading={isLoading}
        />
      </WrapperContainer>
    </PageContainer>
  );
};

export default withSection(MarketManagementPage);
